import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  DraftCreatableWorkflow,
  EditableWorkflow,
  WorkflowState,
} from '../../../../data/models/workflow.model';
import workflowUtils from '../../../../services/workflowUtils';
import SimplifiedWorkflowMonitoring from './SimplifiedWorkflowMonitoring';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import * as monitoringSlice from '../../../../data/monitoring.slice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import simplifiedWorkflowUtils from '../../../../services/simplifiedWorkflowUtils';
import { useTranslation } from 'react-i18next';
import * as lookupSlice from '../../../../data/lookup.slice';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import ConfirmationDialog from '../../../ConfirmationDialog';
import AddWorkFlowDialog from '../AddWorkflowDialog';
import EditSimplifiedWorkflow, {
  EditSimplifiedWorkflowRef,
} from './EditSimplifiedWorkflow';
import WorkflowTabPanel from './WorkflowTabPanel';
import {
  ActionType,
  SendMessageActionFormData,
  SimplifiedWorkflowActionStepFormData,
} from '../../../../data/models/simplified-workflow.model';
import WorkflowExecutions from '../WorkflowExecutions';

interface SimplifiedWorkflowProps {
  isCreate: boolean;
}

const PREFIX = 'SimplifiedWorkflow';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  titleContaner: `${PREFIX}-titleContainer`,
  title: `${PREFIX}-title`,
  menuContainer: `${PREFIX}-menuContainer`,
  stateSelect: `${PREFIX}-stateSelect`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(3),
  },
  [`& .${classes.header}`]: {
    justifyContent: 'space-between',
    alignItems: 'end',
    paddingBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [`& .${classes.titleContaner}`]: {
    paddingTop: theme.spacing(2),
    gap: 10,
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
  [`& .${classes.menuContainer}`]: {
    justifyContent: 'end',
    gap: theme.spacing(2),
  },
  [`& .${classes.stateSelect}`]: {
    width: 144,
  },
}));

export default function SimplifiedWorkflow({
  isCreate,
}: SimplifiedWorkflowProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tabIndex, setTabIndex] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hideWorkQueueSelector, setHideWorkQueueSelector] = useState(false);
  const [addWorkflowDialogOpen, setAddWorkflowDialogOpen] = useState(false);
  const [confirmStatusChangeMessage, setConfirmStatusChangeMessage] =
    useState('');
  const [
    confirmStatusChangeDialogPostiveFn,
    setConfirmStatusChangeDialogPostiveFn,
  ] = useState<() => void>(() => null);
  const [confirmStatusChangeDialogOpen, setConfirmStatusChangeDialogOpen] =
    useState(false);
  const [confirmDeleteWorkflowDialogOpen, setConfirmDeleteWorkflowDialogOpen] =
    useState(false);
  const workflowDraft = useAppSelector(monitoringSlice.selectDraftWorkflow);
  const savingWorkflow = useAppSelector(monitoringSlice.selectSavingWorkflow);
  const createdWorkflow = useAppSelector(monitoringSlice.selectCreatedWorkflow);
  const deletedWorkflow = useAppSelector(monitoringSlice.selectDeletedWorkflow);
  const smartLabels = useAppSelector(lookupSlice.selectCommsCenterSmartLabels);
  const existingWorkflow = useAppSelector((state) => {
    const workflow = monitoringSlice.selectWorkflow(
      state,
      searchParams.get('workflowUUID') ?? '',
    );
    if (workflow) {
      const formWorkflow = simplifiedWorkflowUtils.createWorkflowFormFromModel(
        workflow,
        t,
        smartLabels,
      );
      return formWorkflow;
    }
    return undefined;
  });
  const existingRawWorkflow = useAppSelector((state) => {
    return monitoringSlice.selectWorkflow(
      state,
      searchParams.get('workflowUUID') ?? '',
    );
  });
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const editWorkflowRef = useRef<EditSimplifiedWorkflowRef>();

  const workQueueUUID = searchParams.get('workQueueUUID') ?? '';
  const title = workflowDraft?.name ?? existingWorkflow?.name;
  const description =
    workflowDraft?.description ?? existingWorkflow?.description;

  const shouldFetchWorkflow = useMemo(() => {
    const workflowUUID = searchParams.get('workflowUUID');
    return workflowUUID && !existingWorkflow && smartLabels?.length > 0;
  }, [existingWorkflow, smartLabels]);

  useEffect(() => {
    if (shouldFetchWorkflow) {
      const workflowUUID = searchParams.get('workflowUUID');
      if (workflowUUID) {
        dispatch(monitoringSlice.fetchWorkflowAsync(workflowUUID));
      }
    }
  }, [shouldFetchWorkflow]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('smartLabel'));
  }, []);

  useEffect(() => {
    if (createdWorkflow) {
      navigate(`/simple-notifications`);
      dispatch(monitoringSlice.setCreatedWorkflow(false));
    }
  }, [createdWorkflow]);

  useEffect(() => {
    if (deletedWorkflow) {
      navigate(`/simple-notifications`);
      dispatch(monitoringSlice.setDeletedWorkflow(false));
    }
  }, [deletedWorkflow]);

  useEffect(() => {
    // Need to ask again for the workflow name/description if user refreshes page.
    if (!workflowDraft && isCreate) {
      setHideWorkQueueSelector(true);
      setAddWorkflowDialogOpen(true);
    }
  }, [workflowDraft, isCreate]);

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  let allowedWorkflowStates: WorkflowState[] = [];
  if (existingWorkflow?.state) {
    allowedWorkflowStates = workflowUtils.getAllowedStates(
      existingWorkflow.state,
    );
  }
  const workflowStates = allowedWorkflowStates.map((state) => ({
    key: t(`workflows.${state}`),
    value: state,
  }));

  const handleStateChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event;
    if (!existingWorkflow?.workflowUUID) {
      return;
    }
    let newStateText = '';
    const newState = value as WorkflowState;
    switch (newState) {
      case WorkflowState.Active:
        newStateText = t('workflows.activate');
        break;
      case WorkflowState.Inactive:
        newStateText = t('workflows.deactivate');
        break;
      case WorkflowState.Deleted:
        newStateText = t('workflows.delete');
        break;
    }
    setConfirmStatusChangeMessage(
      t('workflows.confirmStatusChangeMessage', { status: newStateText }),
    );
    setConfirmStatusChangeDialogPostiveFn((prevState) => {
      return () => updateWorkflowState(newState);
    });
    setConfirmStatusChangeDialogOpen(true);
  };

  const handleSaveClick = () => {
    editWorkflowRef.current?.saveWorkflow();
  };

  const handleMoreOptionsClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const handleRenameClick = () => {
    dispatch(
      monitoringSlice.setDraftWorkflow({
        name: title ?? '',
        description,
        workQueueUUID: existingWorkflow?.workQueueUUID ?? workQueueUUID,
      }),
    );
    setAddWorkflowDialogOpen(true);
    setIsMenuOpen(false);
  };

  const handleDeleteWorkflow = () => {
    setIsMenuOpen(false);
    setConfirmDeleteWorkflowDialogOpen(true);
  };

  const handleConfirmDeleteWorkflow = () => {
    setConfirmDeleteWorkflowDialogOpen(false);
    if (existingWorkflow?.workflowUUID) {
      dispatch(
        monitoringSlice.deleteWorkflowAsync(existingWorkflow.workflowUUID),
      );
    }
  };

  const handleAddWorkflowClose = (
    didConfirm?: boolean,
    workflowDraft?: DraftCreatableWorkflow,
  ) => {
    setHideWorkQueueSelector(false);
    setAddWorkflowDialogOpen(false);
    // Immediately save updated name/description
    if (didConfirm && existingRawWorkflow) {
      const updatedWorkflow: EditableWorkflow = {
        name: workflowDraft?.name ?? existingRawWorkflow?.name ?? '',
        description:
          workflowDraft?.description ?? existingRawWorkflow?.description ?? '',
        workQueueUUID: existingRawWorkflow?.workQueueUUID,

        steps: existingRawWorkflow?.steps,
        workflowUUID: existingRawWorkflow?.workflowUUID,
      };
      dispatch(monitoringSlice.updateWorkflowAsync(updatedWorkflow));
    }
  };

  const updateWorkflowState = (newState: WorkflowState) => {
    setConfirmStatusChangeDialogOpen(false);
    if (
      !existingWorkflow ||
      !existingWorkflow.state ||
      !existingWorkflow.workflowUUID
    ) {
      return;
    }
    // Optimistically update state
    dispatch(
      monitoringSlice.updateWorkflowState({
        workflowUUID: existingWorkflow.workflowUUID,
        state: newState,
      }),
    );
    if (newState === WorkflowState.Active) {
      dispatch(
        monitoringSlice.activateWorkflowAsync(existingWorkflow.workflowUUID),
      );
    } else if (newState === WorkflowState.Inactive) {
      dispatch(
        monitoringSlice.deactivateWorkflowAsync(existingWorkflow.workflowUUID),
      );
    } else if (newState === WorkflowState.Deleted) {
      dispatch(
        monitoringSlice.deleteWorkflowAsync(existingWorkflow.workflowUUID),
      );
    }
  };

  if (existingWorkflow?.state === WorkflowState.Deleted) {
    return (
      <Typography variant="h1">{t('workflows.workflowDeleted')}</Typography>
    );
  }

  const messageType = (
    existingWorkflow?.steps.find(
      (step) =>
        (step as SimplifiedWorkflowActionStepFormData).actionType ===
        ActionType.SendMessage,
    ) as SendMessageActionFormData
  )?.messageType;

  return (
    <StyledPaper elevation={2}>
      <Stack direction="column" className={classes.container}>
        <Stack direction="row" className={classes.header}>
          <Stack direction="column" className={classes.titleContaner}>
            <Box>
              <Typography variant="h3" className={classes.title}>
                {title}
              </Typography>
            </Box>
            <Typography>{description}</Typography>
          </Stack>

          <Stack direction="row" className={classes.menuContainer}>
            {!isCreate && existingWorkflow && (
              <FormControl className={classes.stateSelect}>
                <InputLabel id="workflow-status-label">
                  {t('workflows.status')}
                </InputLabel>
                <Select
                  labelId="workflow-status-label"
                  onChange={handleStateChange}
                  value={existingWorkflow.state}
                  disabled={savingWorkflow}>
                  {workflowStates.map((state) => (
                    <MenuItem key={state.key} value={state.value}>
                      {state.key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="contained"
              color="primary"
              disabled={savingWorkflow}
              onClick={handleSaveClick}>
              {t('common.save')}
            </Button>
            <IconButton
              ref={menuButtonRef}
              size="large"
              onClick={handleMoreOptionsClick}>
              <MoreVertIcon />
            </IconButton>
          </Stack>
        </Stack>

        {isCreate && (
          <EditSimplifiedWorkflow
            ref={editWorkflowRef}
            isCreate={isCreate}
            existingWorkflow={existingWorkflow}
            workQueueUUID={workQueueUUID}
          />
        )}
        {!isCreate && (
          <>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth">
              <Tab label={t('simplenotifications.workflow')} />
              <Tab label={t('simplenotifications.monitoring')} />
              <Tab label={t('simplenotifications.executions')} />
            </Tabs>
            <Divider />
            <WorkflowTabPanel value={tabIndex} index={0}>
              <EditSimplifiedWorkflow
                ref={editWorkflowRef}
                isCreate={isCreate}
                existingWorkflow={existingWorkflow}
                workQueueUUID={workQueueUUID}
              />
            </WorkflowTabPanel>
            <WorkflowTabPanel value={tabIndex} index={1}>
              <SimplifiedWorkflowMonitoring
                workflowUUID={existingWorkflow?.workflowUUID ?? ''}
                messageType={messageType}
              />
            </WorkflowTabPanel>
            <WorkflowTabPanel value={tabIndex} index={2}>
              <WorkflowExecutions
                workflowUUID={existingWorkflow?.workflowUUID ?? ''}
              />
            </WorkflowTabPanel>
          </>
        )}
      </Stack>
      <AddWorkFlowDialog
        workQueueUUID={workQueueUUID}
        open={addWorkflowDialogOpen}
        close={handleAddWorkflowClose}
        canSave={!!existingRawWorkflow}
        hideWorkQueueSelector={hideWorkQueueSelector}
      />
      <ConfirmationDialog
        title={t('workflows.confirmStatusChangeTitle')}
        message={confirmStatusChangeMessage}
        open={confirmStatusChangeDialogOpen}
        positiveAction={confirmStatusChangeDialogPostiveFn}
        negativeAction={() => setConfirmStatusChangeDialogOpen(false)}
      />
      <ConfirmationDialog
        title={t('workflows.confirmDeleteWorkflowTitle')}
        message={t('workflows.confirmDeleteWorkflowMessage')}
        open={confirmDeleteWorkflowDialogOpen}
        positiveAction={handleConfirmDeleteWorkflow}
        negativeAction={() => setConfirmDeleteWorkflowDialogOpen(false)}
      />
      <Menu
        anchorEl={menuButtonRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}>
        <MenuItem onClick={handleRenameClick}>{t('workflows.rename')}</MenuItem>
        {!isCreate &&
          (existingWorkflow?.state === WorkflowState.Inactive ||
            existingWorkflow?.state === WorkflowState.Draft) && (
            <MenuItem
              sx={{
                color: 'warning.main',
              }}
              onClick={handleDeleteWorkflow}>
              {t('workflows.deleteWorkflow')}
            </MenuItem>
          )}
      </Menu>
    </StyledPaper>
  );
}
